import { Decimal } from "classes/DecimalClasses";

import { getItemRecord } from "services/sosInventoryService/domainLogic";

import globalState from "globalState/globalState";

import { NO_COLUMNS } from "appConstants";

/**
 * @name    fetchOutboundBinsForLines
 *
 * @summary Processes transaction lines to calculate available bins and normalize
 *          bin selections for each line item. This is a self-contained function
 *          that fetches its own item data, making it ideal for use cases where
 *          line.relatedRecords.item has not been populated yet.
 *
 *          Use this function when:
 *          - You need bin data but haven't called updateLineRelatedRecordsItem() first
 *          - You only need bin information and don't require other item data
 *          - Working with returns/returns to vendor where item data isn't pre-loaded
 *
 *          For each line:
 *          - Fetches current locationBins data for the item via getItemRecord()
 *          - Sets availableBins based on inventory levels and settings
 *          - Normalizes the selected bin (removes invalid selections, sets defaults)
 *
 * @param   lines (array) - transaction lines to process
 * @param   locationId (number) - location ID to get bin data for
 * @param   date (string) - date for inventory calculations
 * @param   maintainBins (boolean) - if true, preserves the existing bin selection and includes it
 *          in availableBins even if it has no inventory; skips default bin normalization
 *
 * @returns Promise<array> - processed lines with updated availableBins and normalized bin selections
 *
 * @see setOutboundBinsForLines - use when line.relatedRecords.item is already populated
 */
export async function fetchOutboundBinsForLines(
  lines,
  locationId,
  date,
  maintainBins = false
) {
  return await Promise.all(
    lines.map(async (line) => {
      if (!line.item?.id) {
        return line;
      }
      const { locationBins } = await getItemRecord(
        line.item.id,
        locationId,
        date,
        ["locationBins"]
      );
      line.availableBins = filterAndFormatBinOptions(
        locationBins,
        maintainBins ? line.bin : null
      );

      if (!maintainBins) {
        // if there is an existing bin value, be sure it is in the list of
        // available bins; if not, null it
        line.bin = await normalizeBin(
          line.bin,
          line.availableBins,
          locationId,
          line.item
        );
      }
      return line;
    })
  );
}

/**
 * @name    setOutboundBinsForLines
 *
 * @summary Processes transaction lines to calculate available bins and normalize
 *          bin selections, using already-fetched item data from line.relatedRecords.item.
 *          This is more efficient when you've already populated item data via
 *          updateLineRelatedRecordsItem().
 *
 *          Use this function when:
 *          - You've already called updateLineRelatedRecordsItem() to populate item data
 *          - You need item data for other calculations (onhand, pricing, etc.) anyway
 *          - Working with shipments, adjustments, transfers, pick tickets, etc.
 *
 *          For each line:
 *          - Uses locationBins from line.relatedRecords.item (must be pre-populated)
 *          - Sets availableBins based on inventory levels and settings
 *          - Normalizes the selected bin (removes invalid selections, sets defaults)
 *
 * @param   location (object) - location reference object
 * @param   lines (array) - transaction lines to process (must have relatedRecords.item populated)
 * @param   binType (string) - property name for the bin field (default: "bin")
 * @param   showAllBins (boolean) - whether to show all bins or filter by inventory
 *
 * @returns Promise<array> - processed lines with updated availableBins and normalized bin selections
 *
 * @see fetchOutboundBinsForLines - use when line.relatedRecords.item is not yet populated
 */
export async function setOutboundBinsForLines(
  location,
  lines,
  binType = "bin",
  showAllBins = false,
  maintainBins = false
) {
  return await Promise.all(
    lines.map(
      async (line) =>
        await setOutboundBinsForLine(
          location,
          line,
          binType,
          showAllBins,
          maintainBins
        )
    )
  );
}

export async function setOutboundBinsForLine(
  location,
  line,
  binType = "bin",
  showAllBins = false,
  maintainBins = false
) {
  const newLine = { ...line };
  const { item } = newLine.relatedRecords;
  // if we have an item record in relatedRecords, set availableBins
  if (item?.id && item?.locationBins) {
    let binOptions;
    if (showAllBins) {
      binOptions = formatBinInfo(item.locationBins);
    } else if (maintainBins) {
      binOptions = filterBinOptionsIncludeSelected(
        item.locationBins,
        line[binType]
      );
    } else {
      binOptions = filterAndFormatBinOptions(item.locationBins);
    }
    newLine.availableBins = binOptions;
  } else {
    newLine.availableBins = [];
  }
  if (!maintainBins) {
    // if there is an existing bin value, be sure it is in the list of
    // available bins; if not, null it
    if (line[binType] && newLine.availableBins) {
      const inInAvailableBins = newLine.availableBins.find(
        (bin) => line[binType].id === bin.id
      );
      if (!inInAvailableBins) {
        line[binType] = null;
      }
    }
    // if there is no existing bin value, set to the default, if any
    if (!line[binType] && item?.id) {
      if (item.bin !== undefined) {
        // item was already fetched with locationId — use its resolved bin directly
        // rather than making another API call
        newLine[binType] = item.bin?.id
          ? newLine.availableBins.find(({ id }) => id === item.bin.id) ?? null
          : null;
      } else {
        newLine[binType] = await getDefaultBin(
          item.id,
          location?.id,
          newLine.availableBins
        );
      }
    }
  }
  return newLine;
}

/**
 * @name    formatBinInfo
 *
 * @summary takes bin information for a location and returns a bins
 * options object as used by our select components, with quantity
 * information included with the bin name
 *
 * @param locationBins (object) - as returned with an item record,
 * when a location and date/time is provided; example:
 *
 * [
 *   { bin: { id: 1, name: "bin1"},
 *     quantity: 123
 *   },
 * ...
 * ]
 *
 * @return an array of bin reference objects, with quantity included
 * with the bin name; example:
 * [
 *   { id: 41, name: "Bin 28 (133)" },
 *   { id: 4, name: "Bin 85 (279)" },
 *   { id: 19, name: "Bin 41 (14)" },
 * ]
 */
export function formatBinInfo(locationBins) {
  return locationBins.map(({ bin, quantity }) => ({
    id: bin.id,
    name: `${bin.name} (${quantity.toString()})`,
  }));
}

/**
 * @name    getDefaultBin
 *
 * @summary returns a bin reference object, which should be the default
 *          provided in the UI when a new inventory item is selected;
 *          it takes into account available bins (which may be limited
 *          due to inventory level) and location, of course
 *
 * @param   itemId (number) - inventory item id
 *
 * @param   locationId (number)
 *
 * @param   availableBins (array of bin reference objects)
 *
 * @returns bin reference object, or null if no default is relevant
 */
export async function getDefaultBin(itemId, locationId, availableBins) {
  if (!itemId || !locationId || availableBins.length === 0) {
    return null;
  }

  const item = await getItemRecord(itemId, locationId, null, [NO_COLUMNS]);

  // if the item has a resolved default bin and it is in the bin options...
  let defaultBin = null;
  if (item?.bin?.id) {
    defaultBin = availableBins.find(({ id }) => id === item.bin.id);
  }
  return defaultBin;
}

export function filterAndFormatBinOptions(locationBins, selectedBin = null) {
  const { preventNegativeInBins } =
    globalState.getState().userCompanySettings.settings;
  if (!preventNegativeInBins) {
    return formatBinInfo(locationBins);
  }

  const binsWithInventory = locationBins.filter((lb) => {
    // Always include the selected bin if provided
    if (selectedBin && lb.bin.id === selectedBin.id) {
      return true;
    }
    return lb.quantity.gt(new Decimal(0));
  });
  return formatBinInfo(binsWithInventory);
}

export function filterBinOptionsIncludeSelected(
  locationBins,
  selectedBin = null
) {
  return filterAndFormatBinOptions(locationBins, selectedBin);
}

export async function getAvailableBins(itemLocationBins) {
  let availableBins = [];
  if (itemLocationBins) {
    const binOptions = filterAndFormatBinOptions(itemLocationBins);
    availableBins = binOptions;
  }
  return availableBins;
}

// Sets each line's bin field to the location's configured default bin.
// If the location has no bin tracking, all bins are cleared.
// binField defaults to "bin" for standard inbound transactions (item receipts, builds)
// and can be set to "toBin" for transfer destinations (transfers, rentals).
export async function setBinForInboundLines(lines, location, binField = "bin") {
  if (!location?.binTracking) {
    return lines.map((line) => ({ ...line, [binField]: null }));
  }
  return await Promise.all(
    lines.map(async (line) => {
      if (!line.item?.id) {
        return { ...line, [binField]: null };
      }
      return {
        ...line,
        [binField]: await fetchDefaultBin(line.item.id, location.id),
      };
    })
  );
}

export async function fetchDefaultBin(itemId, locationId) {
  if (!locationId) {
    return null;
  }
  const item = await getItemRecord(itemId, locationId, null, [NO_COLUMNS]);
  return item?.bin ?? null;
}

async function normalizeBin(bin, availableBins, locationId, item) {
  let newBin = bin;
  if (bin && availableBins) {
    const inInAvailableBins = availableBins.find(({ id }) => id === bin.id);
    if (!inInAvailableBins) {
      newBin = null;
    }
  }
  // if there is no existing bin value, set to the default, if any
  if (!newBin && item?.id) {
    newBin = await getDefaultBin(item.id, locationId, availableBins);
  }
  return newBin;
}
